/**
 * CRDT / manual websocket sync runtime instrumentation — development only.
 *
 * Remove this file + imports when no longer needed.
 *
 * Interpretation guide:
 * - **Normal typing**: `[crdt:update]` bytes often 1–200; `[yjs:text-delta]` shows small retain/insert/delete ops.
 * - **Paste**: one update may be KB-scale but still one logical op; watch for single insert with large string.
 * - **Undo/redo**: similar to paste — can be larger than one character but usually bounded.
 * - **Initial sync / reconnect**: first frames after connect can be large (full or diff state); demo-protocol
 *   sync steps use binary frames — expect larger `maxRecvBytes` spikes then returning to small values.
 * - **Suspicious full replace** (React fallback `updateYFile`): log line `[crdt:react-yfile]` with delete+insert
 *   both large (~full doc length) on every keystroke — indicates whole-string diff from Monaco `value` mode.
 * - **MonacoBinding**: origin often class `MonacoBinding`; `transaction.local === true`; deltas should be small per keystroke.
 * - **Remote edits**: `transaction.local === false` on `doc.on('update')` — definitive for multi-user merges.
 * - **Legacy transport**: if you ever see JSON `delta_apply` or base64 text frames on `[yws:send|recv]`, that path is NOT this stack.
 */

import * as Y from "yjs"
import type { LocalCrdtOrigin } from "@/lib/compiler/types"

/** Dev tracing (set `NEXT_PUBLIC_CRDT_DEBUG=false` to silence in development). */
export const CRDT_RUNTIME_DEBUG =
  typeof process !== "undefined" &&
  process.env.NODE_ENV === "development" &&
  process.env.NEXT_PUBLIC_CRDT_DEBUG !== "false"

const TAG = "[crdt-runtime]"

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now()
}

function ts(): number {
  return Date.now()
}

/** Session-scoped counters; call `reset()` when a new Y.Doc / provider is created. */
export function createCrdtDebugMetrics() {
  let docUpdateEvents = 0
  let docUpdateBytesIn = 0
  let docUpdateBytesOut = 0
  let wsSendFrames = 0
  let wsRecvFrames = 0
  let wsSendBytes = 0
  let wsRecvBytes = 0
  let maxWsSend = 0
  let maxWsRecv = 0
  let reconnectCount = 0
  let awarenessUpdates = 0
  let lastSummaryAt = 0

  return {
    recordDocUpdate(update: Uint8Array, isRemoteTx: boolean) {
      docUpdateEvents += 1
      const n = update.byteLength
      if (isRemoteTx) docUpdateBytesIn += n
      else docUpdateBytesOut += n
    },
    recordWsSend(bytes: number) {
      wsSendFrames += 1
      wsSendBytes += bytes
      maxWsSend = Math.max(maxWsSend, bytes)
    },
    recordWsRecv(bytes: number) {
      wsRecvFrames += 1
      wsRecvBytes += bytes
      maxWsRecv = Math.max(maxWsRecv, bytes)
    },
    bumpReconnect() {
      reconnectCount += 1
    },
    bumpAwareness() {
      awarenessUpdates += 1
    },
    maybeLogSummary() {
      if (!CRDT_RUNTIME_DEBUG) return
      const t = ts()
      if (t - lastSummaryAt < 5000) return
      lastSummaryAt = t
      const avgSend = wsSendFrames ? Math.round(wsSendBytes / wsSendFrames) : 0
      const avgRecv = wsRecvFrames ? Math.round(wsRecvBytes / wsRecvFrames) : 0
      const avgDoc = docUpdateEvents ? Math.round((docUpdateBytesIn + docUpdateBytesOut) / docUpdateEvents) : 0
      console.info(`${TAG} [crdt:metrics]`, {
        docUpdateEvents,
        docUpdateBytesIn,
        docUpdateBytesOut,
        avgDocUpdateBytes: avgDoc,
        wsSendFrames,
        wsRecvFrames,
        avgSendBytes: avgSend,
        avgRecvBytes: avgRecv,
        maxSendBytes: maxWsSend,
        maxRecvBytes: maxWsRecv,
        reconnects: reconnectCount,
        awarenessUpdates,
        timestamp: t,
      })
    },
    reset() {
      docUpdateEvents = 0
      docUpdateBytesIn = 0
      docUpdateBytesOut = 0
      wsSendFrames = 0
      wsRecvFrames = 0
      wsSendBytes = 0
      wsRecvBytes = 0
      maxWsSend = 0
      maxWsRecv = 0
      reconnectCount = 0
      awarenessUpdates = 0
      lastSummaryAt = 0
    },
  }
}

export type CrdtDebugMetrics = ReturnType<typeof createCrdtDebugMetrics>

/** Classify Yjs transaction `origin` (supplement `transaction.local` for source hints). */
export function classifyDocUpdateOrigin(origin: unknown): string {
  if (origin === null || origin === undefined) return "null-origin"
  if (typeof origin === "object" && origin !== null && "kind" in origin) {
    const k = (origin as { kind?: string }).kind
    if (k === "local") return `react-store:${(origin as LocalCrdtOrigin).path ?? "?"}`
    if (k === "seed") return "seed-template-or-reconstruct"
  }
  const ctor = (origin as { constructor?: { name?: string } })?.constructor?.name
  if (ctor === "MonacoBinding") return "monaco-binding"
  if (ctor === "CrdtWsSyncManager") return "crdt-ws-sync-manager"
  if (typeof origin === "string") return `string:${origin}`
  return ctor ?? typeof origin
}

function estimateDocTextChars(filesMap: Y.Map<Y.Text> | null): number {
  if (!filesMap) return 0
  let n = 0
  filesMap.forEach((t) => {
    n += t.length
  })
  return n
}

function serializeTextDelta(event: Y.YTextEvent): unknown[] {
  const out: unknown[] = []
  try {
    event.delta.forEach((op) => {
      if (typeof op === "object" && op !== null) out.push(op)
    })
  } catch {
    return ["<delta iterate error>"]
  }
  return out
}

function flagSuspiciousTextDelta(delta: unknown[], docLen: number): string | undefined {
  let del = 0
  let ins = 0
  for (const op of delta) {
    if (!op || typeof op !== "object") continue
    const o = op as Record<string, unknown>
    if (typeof o.delete === "number") del += o.delete
    if (typeof o.insert === "string") ins += o.insert.length
    if (typeof o.insert !== "string" && o.insert !== undefined) ins += 1
  }
  if (docLen > 500 && del > docLen * 0.5 && ins > docLen * 0.5) {
    return `suspected-large-replace del=${del} ins=${ins} (threshold relative to docLen=${docLen})`
  }
  return undefined
}

/** Binary Yjs update vector applied to the doc (wire encoding — not UTF-16 length). */
export function logDocUpdate(params: {
  update: Uint8Array
  origin: unknown
  transaction: Y.Transaction | null
  filesMap: Y.Map<Y.Text> | null
  mainFileHint: string
  metrics: CrdtDebugMetrics
}) {
  if (!CRDT_RUNTIME_DEBUG) return
  const { update, origin, transaction, filesMap, mainFileHint, metrics } = params
  const originLabel = classifyDocUpdateOrigin(origin)
  const txLocal = transaction?.local
  const isRemoteTx = txLocal === false
  metrics.recordDocUpdate(update, isRemoteTx)
  const docChars = estimateDocTextChars(filesMap)
  console.info(`${TAG} [crdt:update]`, {
    kind: isRemoteTx ? "remote-tx" : txLocal === true ? "local-tx" : "tx-unknown",
    bytes: update.byteLength,
    origin: originLabel,
    transactionLocal: txLocal,
    mainFileHint,
    docTextCharsApprox: docChars,
    timestamp: ts(),
  })
  metrics.maybeLogSummary()
}

export function logReactYFileMutation(params: {
  path: string
  deleteCount: number
  insertLen: number
  prevLen: number
  metrics: CrdtDebugMetrics
}) {
  if (!CRDT_RUNTIME_DEBUG) return
  const { path, deleteCount, insertLen, prevLen, metrics } = params
  const suspicious =
    prevLen > 400 && deleteCount > prevLen * 0.7 && insertLen > prevLen * 0.7
      ? "LIKELY_FULL_DOC_REPLACE_VIA_REACT_FALLBACK"
      : undefined
  console.info(`${TAG} [crdt:react-yfile]`, {
    path,
    deleteCount,
    insertLen,
    prevLen,
    suspicious,
    timestamp: ts(),
  })
  metrics.maybeLogSummary()
}

export function logReactFlushFromYjs(metrics: CrdtDebugMetrics, fileCount: number) {
  if (!CRDT_RUNTIME_DEBUG) return
  console.info(`${TAG} [crdt:react-flush]`, {
    message: "UPSERT all files from Y map → React (debounced batch)",
    fileCount,
    timestamp: ts(),
  })
  metrics.maybeLogSummary()
}

export function logFilesMapStructure(event: Y.YMapEvent<Y.Text>, metrics: CrdtDebugMetrics) {
  if (!CRDT_RUNTIME_DEBUG) return
  const keys: { key: string; action: unknown }[] = []
  // event.keys is Map<string, { action, oldValue }> — forEach(value, key), not (actionString, key).
  event.keys.forEach((keyChange, key) => {
    keys.push({ key, action: keyChange.action })
  })
  console.info(`${TAG} [yjs:files-map]`, {
    keysChanged: keys,
    transactionLocal: event.transaction?.local,
    timestamp: ts(),
  })
  metrics.maybeLogSummary()
}

export function logYTextDelta(path: string, event: Y.YTextEvent, metrics: CrdtDebugMetrics) {
  if (!CRDT_RUNTIME_DEBUG) return
  const delta = serializeTextDelta(event)
  const docLen = event.target.length
  const suspicious = flagSuspiciousTextDelta(delta as unknown[], docLen)
  console.info(`${TAG} [yjs:text-delta]`, {
    path,
    delta,
    targetLen: docLen,
    transactionLocal: event.transaction?.local,
    suspicious,
    timestamp: ts(),
  })
  metrics.maybeLogSummary()
}

export function logTransact(label: string, origin: unknown, fn: () => void) {
  if (!CRDT_RUNTIME_DEBUG) {
    fn()
    return
  }
  const t0 = nowMs()
  fn()
  console.info(`${TAG} [yjs:transact]`, {
    label,
    origin: classifyDocUpdateOrigin(origin),
    durationMs: Math.round(nowMs() - t0),
    timestamp: ts(),
  })
}

export function logYwsSync(params: {
  synced: boolean
  roomName: string
  filesMap: Y.Map<Y.Text> | null
  sinceConnectMs?: number
  metrics: CrdtDebugMetrics
}) {
  if (!CRDT_RUNTIME_DEBUG) return
  const { synced, roomName, filesMap, sinceConnectMs, metrics } = params
  console.info(`${TAG} [yws:sync]`, {
    synced,
    roomName,
    docTextCharsApprox: estimateDocTextChars(filesMap),
    sinceConnectMs,
    timestamp: ts(),
  })
  metrics.maybeLogSummary()
}

export function logAwarenessUpdate(metrics: CrdtDebugMetrics, stateCount: number) {
  if (!CRDT_RUNTIME_DEBUG) return
  metrics.bumpAwareness()
  console.info(`${TAG} [crdt:awareness]`, {
    stateCount,
    timestamp: ts(),
  })
  metrics.maybeLogSummary()
}

/** Wrap native WebSocket for binary send/recv logging (CRDT sync uses binary frames). */
export function createInstrumentedWebSocketClass(
  Base: typeof WebSocket,
  metrics: CrdtDebugMetrics,
): typeof WebSocket {
  if (!CRDT_RUNTIME_DEBUG) return Base

  class InstrumentedWebSocket extends Base {
    private _wrappedMessageHandler: ((this: WebSocket, ev: MessageEvent) => void) | null = null

    constructor(url: string | URL, protocols?: string | string[]) {
      super(url, protocols as string | string[] | undefined)
    }

    send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
      const { kind, bytes, binary } = describeWsPayload(data)
      metrics.recordWsSend(bytes)
      console.info(`${TAG} [yws:send]`, { bytes, binary, kind, timestamp: ts() })
      if (kind === "json_string" || kind === "string") {
        console.warn(`${TAG} [yws:send] unexpected text frame — CRDT sync should use binary`, {
          preview: typeof data === "string" ? data.slice(0, 120) : kind,
        })
      }
      metrics.maybeLogSummary()
      super.send(data as never)
    }

    set onmessage(handler: ((this: WebSocket, ev: MessageEvent) => void) | null) {
      this._wrappedMessageHandler = handler
      if (!handler) {
        super.onmessage = null
        return
      }
      super.onmessage = (ev: MessageEvent) => {
        const { kind, bytes, binary } = describeMessageEventData(ev.data)
        metrics.recordWsRecv(bytes)
        console.info(`${TAG} [yws:recv]`, { bytes, binary, kind, timestamp: ts() })
        if (kind === "json_string" || kind === "string") {
          console.warn(`${TAG} [yws:recv] unexpected text frame — expect ArrayBuffer/Blob for Yjs sync`, {
            preview: typeof ev.data === "string" ? ev.data.slice(0, 120) : kind,
          })
        }
        metrics.maybeLogSummary()
        handler.call(this, ev)
      }
    }

    get onmessage(): ((this: WebSocket, ev: MessageEvent) => void) | null {
      return this._wrappedMessageHandler
    }
  }

  return InstrumentedWebSocket as unknown as typeof WebSocket
}

function describeWsPayload(data: string | ArrayBufferLike | Blob | ArrayBufferView): {
  kind: string
  bytes: number
  binary: boolean
} {
  if (typeof data === "string") {
    const looksJson = data.trimStart().startsWith("{") || data.trimStart().startsWith("[")
    return { kind: looksJson ? "json_string" : "string", bytes: data.length, binary: false }
  }
  if (data instanceof ArrayBuffer) return { kind: "ArrayBuffer", bytes: data.byteLength, binary: true }
  if (ArrayBuffer.isView(data)) return { kind: "ArrayBufferView", bytes: data.byteLength, binary: true }
  if (data instanceof Blob) return { kind: "Blob", bytes: data.size, binary: true }
  return { kind: typeof data, bytes: 0, binary: false }
}

function describeMessageEventData(data: unknown): { kind: string; bytes: number; binary: boolean } {
  if (data instanceof ArrayBuffer) return { kind: "ArrayBuffer", bytes: data.byteLength, binary: true }
  if (ArrayBuffer.isView(data)) return { kind: "ArrayBufferView", bytes: data.byteLength, binary: true }
  if (typeof Blob !== "undefined" && data instanceof Blob) return { kind: "Blob", bytes: data.size, binary: true }
  if (typeof data === "string") {
    const looksJson = data.trimStart().startsWith("{")
    return { kind: looksJson ? "json_string" : "string", bytes: data.length, binary: false }
  }
  return { kind: typeof data, bytes: 0, binary: false }
}

export function clearYTextDebugRegistry(registry: Map<string, () => void>): void {
  registry.forEach((stop) => {
    try {
      stop()
    } catch {
      /* ignore */
    }
  })
  registry.clear()
}

export function primeYTextDebugRegistry(
  filesMap: Y.Map<Y.Text>,
  metrics: CrdtDebugMetrics,
  registry: Map<string, () => void>,
): void {
  if (!CRDT_RUNTIME_DEBUG) return
  filesMap.forEach((ytext, key) => {
    if (registry.has(key)) return
    const obs = (e: Y.YTextEvent) => {
      logYTextDelta(key, e, metrics)
    }
    ytext.observe(obs)
    registry.set(key, () => {
      ytext.unobserve(obs)
      registry.delete(key)
    })
  })
}
